import { type V2, angleTo, dist, len, normalize, rotateTowards, sub } from '../core/math';
import type { Actor } from './actor';
import { isDisabled } from './combat';
import { getStatus } from './status';
import type { World } from './world';

const TURN_RATE = 18; // rad/s when walking
const ARRIVE = 0.12;

function isRooted(a: Actor): boolean {
  return a.statuses.some((s) => getStatus(s.id).cc?.root);
}

/** Moves a locally-owned actor for one tick. */
export function updateMovement(world: World, a: Actor, dt: number): void {
  a.speed = 0;
  if (!a.alive) return;

  // Forced displacement (dash / knockback / pull) overrides everything.
  if (a.displacement) {
    const d = a.displacement;
    const step = Math.min(dt, d.remaining);
    const delta = { x: d.vx * step, z: d.vz * step };
    a.pos = d.ignoreCollision ? world.nav.clampToBounds({ x: a.pos.x + delta.x, z: a.pos.z + delta.z }) : world.nav.slide(a.pos, delta);
    if (d.ignoreCollision && !world.nav.walkable(a.pos)) {
      // Never end a dash inside a wall.
      if (d.remaining - step <= 0) a.pos = world.nav.nearestWalkable(a.pos) ?? a.pos;
    }
    a.speed = Math.hypot(d.vx, d.vz);
    d.remaining -= step;
    if (d.remaining <= 1e-6) a.displacement = null;
  }

  // Launch arc / knock-down timers.
  if (a.airTimer > 0) {
    a.airTimer -= dt;
    const t = 1 - Math.max(0, a.airTimer) / Math.max(0.01, a.airTotal);
    a.height = Math.sin(Math.PI * t) * 1.6;
    if (a.airTimer <= 0) {
      a.height = 0;
      a.downTimer = Math.max(a.downTimer, 0.6);
    }
    return;
  }
  a.height = 0;
  if (a.downTimer > 0) {
    a.downTimer -= dt;
    return;
  }
  if (a.flinchTimer > 0) {
    a.flinchTimer -= dt;
    return;
  }
  if (a.displacement || isDisabled(a) || isRooted(a)) return;

  // Fear: run away from the source.
  const fear = a.statuses.find((s) => getStatus(s.id).cc?.fear);
  if (fear) {
    const src = world.actors.get(fear.sourceId);
    if (src) {
      const away = normalize(sub(a.pos, src.pos));
      step(world, a, away, a.stats.moveSpeed * 0.8, dt);
    }
    return;
  }

  let moveMult = 1;
  if (a.cast) {
    const c = a.cast;
    if (c.phase === 'holding') moveMult = c.skill.holding?.moveMult ?? 0;
    else if (c.phase === 'stage') moveMult = c.skill.stages[c.stage]?.moveMult ?? 0;
    else moveMult = 0;
    if (moveMult <= 0) return;
  }

  const speed = a.stats.moveSpeed * moveMult;
  if (a.moveDir) {
    step(world, a, a.moveDir, speed, dt);
    return;
  }
  const next = a.path[0];
  if (!next) return;
  const to = sub(next, a.pos);
  const d = len(to);
  if (d <= ARRIVE) {
    a.path.shift();
    if (a.path.length === 0) a.moveGoal = null;
    return;
  }
  const dir = { x: to.x / d, z: to.z / d };
  const maxStep = speed * dt;
  if (maxStep >= d) {
    a.pos = world.nav.slide(a.pos, to);
    a.path.shift();
    if (a.path.length === 0) a.moveGoal = null;
    if (!a.cast) a.facing = rotateTowards(a.facing, angleTo({ x: 0, z: 0 }, dir), TURN_RATE * dt);
    a.speed = speed;
    return;
  }
  step(world, a, dir, speed, dt);
}

function step(world: World, a: Actor, dir: V2, speed: number, dt: number): void {
  const before = { ...a.pos };
  a.pos = world.nav.slide(a.pos, { x: dir.x * speed * dt, z: dir.z * speed * dt });
  const moved = dist(before, a.pos);
  a.speed = moved / Math.max(dt, 1e-6);
  if (!a.cast && moved > 1e-4) a.facing = rotateTowards(a.facing, Math.atan2(dir.x, dir.z), TURN_RATE * dt);
  if (moved < 1e-4 && a.path.length > 0) {
    // Stuck against geometry: re-plan once.
    const goal = a.moveGoal;
    a.path = goal ? (world.nav.findPath(a.pos, goal) ?? []) : [];
  }
}

/**
 * Soft separation so actors do not stack. Bosses are immovable; players
 * push monsters less than monsters push each other.
 */
export function separate(world: World): void {
  const list = world.aliveActors;
  for (const a of list) {
    if (!world.isLocal(a) || a.displacement) continue;
    const near = world.spatial.query(a.pos, a.radius + 2);
    for (const b of near) {
      if (b === a || !b.alive || b.kind === 'npc') continue;
      const minD = a.radius + b.radius;
      const dx = a.pos.x - b.pos.x;
      const dz = a.pos.z - b.pos.z;
      const d2 = dx * dx + dz * dz;
      if (d2 >= minD * minD || d2 < 1e-8) continue;
      const d = Math.sqrt(d2);
      const push = (minD - d) * 0.5;
      const aHeavy = a.monster?.immunePush ?? false;
      if (aHeavy) continue;
      const bHeavy = b.monster?.immunePush ?? false;
      const k = bHeavy ? 1 : a.team === b.team ? 0.5 : 0.35;
      a.pos = world.nav.slide(a.pos, { x: (dx / d) * push * k * 2, z: (dz / d) * push * k * 2 });
    }
  }
}
